import { isNetworkFailure } from '../../../tel/src/fetch'
import type {
  AnyRouter,
  AnyProcedure,
  BatchResponse,
  SubscriptionHandlers,
} from '../core/types'
import {
  deserializeRpcError,
  RpcError,
  type SerializedError,
} from '../core/error'
import {
  createSubscriptionHandle,
  readSubscriptionStream,
} from './subscription'
import { type BatchOperation, type BatchResult } from './batch-link'
import {
  createOperationTransport,
  type FetchFn,
  type OnlineDetector,
  type RetryFetchOptions,
} from '@w/utils'
import {
  createRelayFetch,
  getRelayFetchTel,
  insertRelayedTelemetry,
  nullTel,
  type BoundaryTelemetry,
  type Tel,
} from '@w/tel'

export interface RpcCallOptions {
  tel?: Tel
}

export interface RpcRequestOptions extends RpcCallOptions {
  signal?: AbortSignal
}

type WithCallOptions<TFn, TOptions = RpcCallOptions> = TFn extends (...args: infer TArgs) => infer TResult
  ? (...args: [...TArgs, opts?: TOptions]) => TResult
  : never

type ClientRouterProcedures<TRouter extends AnyRouter> = {
  [K in keyof TRouter]: TRouter[K] extends AnyProcedure
    ? {
        query: WithCallOptions<TRouter[K]['_types']['query'], RpcRequestOptions>
        mutate: WithCallOptions<TRouter[K]['_types']['mutate'], RpcRequestOptions>
        subscribe: WithCallOptions<TRouter[K]['_types']['subscribe']>
      }
    : TRouter[K] extends AnyRouter
      ? ClientRouterProcedures<TRouter[K]>
      : never
}

export type CreateClientRouter<TRouter extends AnyRouter> =
  ClientRouterProcedures<TRouter> & {
    withTel(tel: Tel): RpcClient<TRouter>
  }

export type RpcClient<TRouter extends AnyRouter> = CreateClientRouter<TRouter>

export interface RpcClientConfig {
  baseUrl?: string
  headers?: Record<string, string>
  fetch?: FetchFn
  retry?: RetryFetchOptions
  onlineDetector?: OnlineDetector
  tel?: Tel
  batching?: {
    enabled: boolean
    timeoutMs?: number
    maxBatchSize?: number
    maxUrlLength?: number
  }
}

export function createRpcClient<TRouter extends AnyRouter>(
  config: RpcClientConfig,
): RpcClient<TRouter> {
  const baseUrl = (config.baseUrl ?? '').replace(/\/$/, '')
  const rpcUrl = `${baseUrl}/rpc`
  const batching = config.batching
  const defaultTel = config.tel ?? nullTel
  const recordedErrors = new WeakSet<object>()
  const recordError = <T>(tel: Tel, error: T, operation = 'rpc.subscription'): T => {
    if (typeof error === 'object' && error !== null) {
      if (recordedErrors.has(error)) return error
      recordedErrors.add(error)
    }
    if (error instanceof RpcError && isExpectedFailure(error)) {
      tel.warn(error.message, { status: error.status, code: error.code })
      return error
    }
    if (isExpectedFailure(error)) {
      tel.warn('RPC network unavailable', { operation, errorName: error instanceof Error ? error.name : 'Error' })
      return error
    }
    return tel.error(error)
  }
  const transports = new WeakMap<Tel, ReturnType<typeof makeTransport>>()

  function makeTransport(tel: Tel, signal?: AbortSignal) {
    const fetch = instrumentRpcFetch(config.fetch ?? globalThis.fetch, tel)
    return createOperationTransport<BatchOperation, BatchResult>({
      fetch,
      retry: config.retry,
      onlineDetector: config.onlineDetector,
      execute: async (operation, ctx) => {
        const [result] = await executeRpcOperations(
          [operation],
          rpcUrl,
          config,
          signal ? (input, init) => ctx.fetch(input, { ...init, signal }) : ctx.fetch,
        )
        if (!result) {
          throw new Error('Request failed')
        }
        return result
      },
    })
  }

  function getTransport(tel: Tel) {
    const existing = transports.get(tel)
    if (existing) return existing
    const transport = makeTransport(tel)
    transports.set(tel, transport)
    return transport
  }

  const batcher = batching?.enabled
    ? createRpcBatcher(batching, (riders, signal) =>
        dispatchBatch(riders, signal, rpcUrl, config, makeTransport))
    : undefined

  const executeRequest = async (
    path: string,
    input: unknown,
    type: 'query' | 'mutation',
    tel: Tel,
    signal?: AbortSignal,
  ) => {
    signal?.throwIfAborted()
    if (batcher) return executeBatchedRequest(batcher, path, input, type, tel, signal)
    try {
      const transport = signal ? makeTransport(tel, signal) : getTransport(tel)
      const result = await transport.execute({ path, input, type })

      if (!result.ok) {
        throw deserializeRpcError(result.error)
      }

      return result.data
    } catch (error) {
      if (signal?.aborted) throw error
      throw recordError(tel, error, `rpc.${path}`)
    }
  }

  // DataLoader / tRPC httpBatchLink: one batch per client per tick across
  // every caller. Each call keeps a `rpc.<path>` span in its caller's trace.
  const executeBatchedRequest = async (
    rpcBatcher: ReturnType<typeof createRpcBatcher>,
    path: string,
    input: unknown,
    type: 'query' | 'mutation',
    tel: Tel,
    signal?: AbortSignal,
  ) => {
    const callTel = tel === nullTel
      ? nullTel
      : tel._createTel({ name: `rpc.${path}`, type, input, trace: !tel._getBaggage().traceId })
    try {
      const result = await rpcBatcher.load({ operation: { path, input, type }, tel: callTel, signal })
      if (!result.ok) {
        throw deserializeRpcError(result.error)
      }
      if (callTel !== nullTel) callTel.currentSpan.end({ output: { ok: true } })
      return result.data
    } catch (error) {
      if (signal?.aborted) {
        if (callTel !== nullTel) {
          callTel.currentSpan.end({ attributes: { 'rpc.aborted': true } })
        }
        throw error
      }
      const recorded = recordError(callTel, error, `rpc.${path}`)
      if (callTel !== nullTel) {
        callTel.currentSpan.end({ error: recorded }, {
          errorLevel: isExpectedFailure(recorded) ? 'warning' : 'error',
        })
      }
      throw recorded
    }
  }

  // Subscriptions bypass batching and the retry link: a retried POST would
  // re-invoke a handler that may already have run side effects.
  const subscribe = (
    path: string,
    input: unknown,
    tel: Tel,
    // oxlint-disable-next-line typescript/no-explicit-any -- generic RPC item type
    handlers?: SubscriptionHandlers<any>,
  ) => {
    const rawFetch = instrumentRpcFetch(config.fetch ?? globalThis.fetch, tel)
    return createSubscriptionHandle(async (sink, signal) => {
      try {
        const response = await rawFetch(`${rpcUrl}/${path}`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...config.headers,
          },
          body: JSON.stringify(rpcJsonCodec.serialize({ input, type: 'subscription' })),
          signal,
        })

        if (!response.ok) {
          sink.end(recordError(tel, await rpcErrorFromResponse(response)))
          return
        }

        if (!response.body) {
          sink.end(
            recordError(
              tel,
              new RpcError(500, 'Subscription response has no body'),
            ),
          )
          return
        }

        await readSubscriptionStream(
          response.body,
          {
            item: (value) => {
              sink.item(value)
            },
            end: (error) => {
              sink.end(error ? recordError(tel, error) : undefined)
            },
          },
          signal,
          (telemetry) => {
            getRelayFetchTel(response)?._insertBoundaryTrace(
              telemetry.trace,
              telemetry.metrics,
              telemetry.resource,
            )
          },
        )
      } catch (error) {
        if (signal.aborted) throw error
        throw recordError(tel, error, `rpc.${path}`)
      }
    }, handlers)
  }

  // oxlint-disable-next-line typescript/no-explicit-any -- recursive proxy builder returns dynamic type
  const buildProxy = (basePath: string[] = [], boundTel = defaultTel): any => {
    return new Proxy(
      {},
      {
        get(_target, prop: string) {
          if (basePath.length === 0 && prop === 'withTel') {
            return (tel: Tel) => buildProxy([], tel)
          }
          const currentPath = [...basePath, prop]

          return new Proxy(
            {},
            {
              get(_innerTarget, method: string) {
                if (method === 'subscribe') {
                  return (
                    input: unknown,
                    handlers?: SubscriptionHandlers<unknown>,
                    opts?: RpcCallOptions,
                  ) =>
                    subscribe(
                      currentPath.join('.'),
                      input,
                      opts?.tel ?? boundTel,
                      handlers,
                    )
                }

                if (method === 'query' || method === 'mutate') {
                  // oxlint-disable-next-line typescript/no-explicit-any -- generic RPC input type
                  return (input: any, opts?: RpcRequestOptions) =>
                    executeRequest(
                      currentPath.join('.'),
                      input,
                      method === 'query' ? 'query' : 'mutation',
                      opts?.tel ?? boundTel,
                      opts?.signal,
                    )
                }

                // Assume it's a nested router. The key to descend into is the
                // one read off this proxy (`method`); `prop` is the parent
                // segment already baked into `currentPath`.
                return buildProxy(currentPath, boundTel)[method]
              },
            },
          )
        },
      },
    )
  }

  return buildProxy()
}

function instrumentRpcFetch(fetchFn: FetchFn, tel: Tel): FetchFn {
  if (tel === nullTel) return fetchFn
  return (input, init) => {
    const instrumented = createRelayFetch({
      tel,
      mode: 'native',
      fetch: (tracedInput: RequestInfo | URL) => {
        const tracedRequest = new Request(tracedInput)
        return fetchFn(input, {
          ...init,
          headers: tracedRequest.headers,
        })
      },
    })
    return instrumented(input, init)
  }
}

function isExpectedFailure(error: unknown): boolean {
  if (error instanceof RpcError) return error.status >= 400 && error.status < 500
  return isNetworkFailure(error)
}

type BatchingConfig = NonNullable<RpcClientConfig['batching']>

interface BatchRider {
  operation: BatchOperation
  tel: Tel
  signal?: AbortSignal
  sent: boolean
  aborted: boolean
  abortBatch?: () => void
  resolve(result: BatchResult): void
  reject(error: unknown): void
}

type DispatchBatch = (
  riders: BatchRider[],
  signal: AbortSignal | undefined,
) => Promise<BatchResult[]>

/**
 * Per-client, per-tick batcher (DataLoader, tRPC httpBatchLink). A rider
 * aborted before its batch is sent leaves the batch; one aborted after send
 * rejects alone, and the request is aborted only when every rider aborted.
 */
function createRpcBatcher(batching: BatchingConfig, dispatch: DispatchBatch) {
  const pending = new Map<BatchOperation['type'], BatchRider[]>()

  function flush(type: BatchOperation['type']) {
    const riders = pending.get(type) ?? []
    pending.delete(type)
    let group: BatchRider[] = []
    for (const rider of riders) {
      if (validateBatchOperations([...group, rider].map((it) => it.operation), batching)) {
        group.push(rider)
        continue
      }
      if (group.length > 0) send(group)
      group = [rider]
      if (!validateBatchOperations([rider.operation], batching)) {
        rider.reject(new Error('Input is too big for a single dispatch'))
        group = []
      }
    }
    if (group.length > 0) send(group)
  }

  function send(riders: BatchRider[]) {
    const controller = riders.some((rider) => rider.signal) ? new AbortController() : undefined
    for (const rider of riders) {
      rider.sent = true
      rider.abortBatch = () => {
        if (riders.every((it) => it.aborted)) controller?.abort(rider.signal?.reason)
      }
    }
    dispatch(riders, controller?.signal).then(
      (results) => {
        for (const [index, rider] of riders.entries()) {
          const result = results[index]
          if (result) rider.resolve(result)
          else rider.reject(new Error('Missing result'))
        }
        return null
      },
      (error: unknown) => {
        for (const rider of riders) rider.reject(error)
      },
    )
  }

  function load(call: Pick<BatchRider, 'operation' | 'tel' | 'signal'>): Promise<BatchResult> {
    const { promise, resolve, reject } = Promise.withResolvers<BatchResult>()
    const signal = call.signal
    const onAbort = () => {
      rider.aborted = true
      if (!rider.sent) {
        const queue = pending.get(call.operation.type)
        const index = queue?.indexOf(rider) ?? -1
        if (index >= 0) queue?.splice(index, 1)
      }
      reject(signal?.reason)
      rider.abortBatch?.()
    }
    const rider: BatchRider = {
      ...call,
      sent: false,
      aborted: false,
      resolve(result) {
        signal?.removeEventListener('abort', onAbort)
        if (!rider.aborted) resolve(result)
      },
      reject(error) {
        signal?.removeEventListener('abort', onAbort)
        if (!rider.aborted) reject(error)
      },
    }
    signal?.addEventListener('abort', onAbort, { once: true })
    let queue = pending.get(call.operation.type)
    if (!queue) {
      queue = []
      pending.set(call.operation.type, queue)
      setTimeout(() => {
        flush(call.operation.type)
      }, batching.timeoutMs ?? 0)
    }
    queue.push(rider)
    return promise
  }

  return { load }
}

/**
 * The physical request span goes to the first recording rider's trace
 * (OTel messaging batch rule: one send span, riders link to it). Riders
 * record `rpc.batch.id` = that span id, since `SpanData` has no links.
 */
async function dispatchBatch(
  riders: BatchRider[],
  signal: AbortSignal | undefined,
  rpcUrl: string,
  config: RpcClientConfig,
  makeTransport: (tel: Tel) => { fetch: FetchFn },
): Promise<BatchResult[]> {
  const operations = riders.map((rider) => rider.operation)
  const lead = riders.find((rider) => rider.tel !== nullTel)
  let batchId: string | undefined
  const leadTel = lead
    ? _withPhysicalSpanAttributes(lead.tel, operations, (id) => {
        batchId = id
      })
    : nullTel
  const transportFetch = makeTransport(leadTel).fetch
  const fetchFn: FetchFn = signal
    ? (input, init) => transportFetch(input, { ...init, signal })
    : transportFetch
  const stamp = () => {
    if (!lead) return
    for (const [index, rider] of riders.entries()) {
      if (rider.tel === nullTel) continue
      rider.tel.currentSpan.update({
        attributes: {
          ...(batchId === undefined ? {} : { 'rpc.batch.id': batchId }),
          'rpc.batch.index': index,
        },
      })
    }
  }
  try {
    return await executeRpcOperations(operations, rpcUrl, config, fetchFn, riders.map((rider) => rider.tel))
  } finally {
    stamp()
  }
}

function _withPhysicalSpanAttributes(
  tel: Tel,
  operations: BatchOperation[],
  onSpan: (id: string) => void,
): Tel {
  const wrapped = Object.create(tel) as Tel
  wrapped._createTel = (opts) => {
    const child = tel._createTel({
      ...opts,
      attributes: {
        ...opts.attributes,
        'rpc.batch.size': operations.length,
        'rpc.batch.paths': operations.map((operation) => operation.path),
      },
    })
    onSpan(child.currentSpan.data.id)
    return child
  }
  return wrapped
}

/**
 * Splits the server's one `rpc.request` unit: each rider's server
 * `rpc.<path>` subtree goes under that rider's span (its own trace); the
 * rest stays under the physical fetch span.
 */
function insertBatchTelemetry(
  response: Response,
  payload: BatchResponse,
  operations: BatchOperation[],
  riderTels: Tel[],
): BatchResult[] {
  const telemetry = payload.telemetry
  const fetchTel = getRelayFetchTel(response)
  if (!telemetry || !fetchTel) return insertRelayedTelemetry(response, payload).results
  const children = [...telemetry.trace.children]
  const perRider = operations.map((operation, index): BoundaryTelemetry['trace'] | null => {
    if (riderTels[index] === nullTel || riderTels[index] === undefined) return null
    const at = children.findIndex((child) => child.name === `rpc.${operation.path}`)
    return at < 0 ? null : children.splice(at, 1)[0] ?? null
  })
  fetchTel._insertBoundaryTrace(
    { ...telemetry.trace, children },
    telemetry.metrics,
    telemetry.resource,
  )
  for (const [index, subtree] of perRider.entries()) {
    if (subtree) riderTels[index]?._insertBoundaryTrace(subtree, [], telemetry.resource)
  }
  return payload.results
}

function validateBatchOperations(
  operations: BatchOperation[],
  batching: NonNullable<RpcClientConfig['batching']>,
): boolean {
  const maxBatchSize = batching.maxBatchSize ?? 100
  const maxUrlLength = batching.maxUrlLength ?? 2048

  if (operations.length > maxBatchSize) {
    return false
  }

  if (operations[0]?.type !== 'query') {
    return true
  }

  return estimateBatchUrlLength(operations) <= maxUrlLength
}

function estimateBatchUrlLength(operations: BatchOperation[]): number {
  const batchParam = encodeURIComponent(JSON.stringify(rpcJsonCodec.serialize(operations)))
  return batchParam.length + 50
}

async function executeRpcOperations(
  operations: BatchOperation[],
  rpcUrl: string,
  config: RpcClientConfig,
  fetchFn: FetchFn,
  riderTels: Tel[] = [],
): Promise<BatchResult[]> {
  const operation = operations[0]
  if (!operation) {
    throw new Error('Request failed')
  }

  if (operations.length === 1) {
    return [await executeSingleRpcOperation(operation, rpcUrl, config, fetchFn)]
  }

  if (operation.type === 'query') {
    const batchParam = encodeURIComponent(JSON.stringify(rpcJsonCodec.serialize(operations)))
    const response = await fetchFn(`${rpcUrl}/_batch?batch=${batchParam}`, {
      method: 'GET',
      headers: config.headers,
    })

    if (!response.ok) {
      throw await rpcErrorFromResponse(response)
    }

    const payload = rpcJsonCodec.deserialize(await response.json()) as BatchResponse
    return insertBatchTelemetry(response, payload, operations, riderTels)
  }

  const response = await fetchFn(`${rpcUrl}/_batch`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...config.headers,
    },
    body: JSON.stringify(rpcJsonCodec.serialize({ batch: operations })),
  })

  if (!response.ok) {
    throw await rpcErrorFromResponse(response)
  }

  const payload = rpcJsonCodec.deserialize(await response.json()) as BatchResponse
  return insertBatchTelemetry(response, payload, operations, riderTels)
}

async function executeSingleRpcOperation(
  operation: BatchOperation,
  rpcUrl: string,
  config: RpcClientConfig,
  fetchFn: FetchFn,
): Promise<BatchResult> {
  const response = await fetchFn(`${rpcUrl}/${operation.path}`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...config.headers,
    },
    body: JSON.stringify(rpcJsonCodec.serialize({ input: operation.input, type: operation.type })),
  })

  if (!response.ok) {
    throw await rpcErrorFromResponse(response)
  }

  const payload = rpcJsonCodec.deserialize(await response.json()) as BatchResult
  return insertRelayedTelemetry(response, payload)
}

async function rpcErrorFromResponse(response: Response): Promise<RpcError> {
  const body = rpcJsonCodec.deserialize(await response.json().catch(() => null)) as {
    error?: SerializedError
    telemetry?: BoundaryTelemetry
  } | null
  insertRelayedTelemetry(response, body ?? undefined)
  return deserializeRpcError(body?.error, response.status)
}

// Reserved tags are escaped so ordinary records, including numeric keys, stay records.
export const rpcJsonCodec = {
  serialize(value: unknown): unknown {
    if (value instanceof Uint8Array) return { $rpc: 'bytes', value: Array.from(value) }
    if (Array.isArray(value)) return value.map((item: unknown) => rpcJsonCodec.serialize(item))
    if (value !== null && typeof value === 'object') {
      const entries = Object.entries(value).map(([key, item]) => [key, rpcJsonCodec.serialize(item)])
      return Object.hasOwn(value, '$rpc')
        ? { $rpc: 'object', value: entries }
        : Object.fromEntries(entries)
    }
    return value
  },
  deserialize(value: unknown): unknown {
    if (Array.isArray(value)) return value.map((item: unknown) => rpcJsonCodec.deserialize(item))
    if (value !== null && typeof value === 'object') {
      const record = value as Record<string, unknown>
      if (record.$rpc === 'bytes' && Array.isArray(record.value)) {
        if (!record.value.every((byte: unknown) => typeof byte === 'number' && Number.isInteger(byte) && byte >= 0 && byte <= 255)) {
          throw new Error('Invalid RPC binary encoding')
        }
        return new Uint8Array(record.value as number[])
      }
      if (record.$rpc === 'object' && Array.isArray(record.value)) {
        return Object.fromEntries((record.value as [string, unknown][]).map(([key, item]) => [key, rpcJsonCodec.deserialize(item)]))
      }
      return Object.fromEntries(Object.entries(record).map(([key, item]) => [key, rpcJsonCodec.deserialize(item)]))
    }
    return value
  },
}
